
import { useState, useEffect } from 'react';
import SEO from '../../components/SEO';

export default function About() {
  const [hoveredLeader, setHoveredLeader] = useState<number | null>(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const philosophicalQuestions = [
    "What if education could rewrite destiny?",
    "Can technology truly democratize opportunity?",
    "What happens when passion meets purpose?",
    "How do we build the future we want to see?",
    "What if every mind could unlock its potential?"
  ];

  const leaders = [
    {
      name: "Abdullahi Saidu",
      role: "Founder & Visionary",
      image: "https://readdy.ai/api/search-image?query=Professional%20memoji%20avatar%20of%20young%20African%20male%20tech%20entrepreneur%2C%20confident%20expression%2C%20modern%20business%20attire%2C%20purple%20lighting%20accent%2C%203D%20cartoon%20style%20memoji%20character%2C%20leadership%20presence%2C%20clean%20background&width=400&height=500&seq=25&orientation=portrait",
      quote: "We don't wait for opportunity — we architect it.",
      bio: "Revolutionary leader reshaping Africa's tech landscape. Believes in the power of education to transform entire generations.",
      philosophy: "Every line of code written is a step toward African technological sovereignty."
    },
    {
      name: "Aysha Idris",
      role: "Co-Founder & Strategy",
      image: "https://readdy.ai/api/search-image?query=Professional%20memoji%20avatar%20of%20young%20African%20female%20tech%20leader%2C%20confident%20smile%2C%20modern%20business%20attire%2C%20purple%20and%20blue%20lighting%2C%203D%20cartoon%20style%20memoji%20character%2C%20innovative%20co-founder%20presence%2C%20clean%20background&width=400&height=500&seq=26&orientation=portrait",
      quote: "Innovation happens when passion collides with purpose.",
      bio: "Strategic mastermind building sustainable tech ecosystems. Expert in scaling transformative educational experiences.",
      philosophy: "True innovation isn't just about technology — it's about human potential."
    },
    {
      name: "Aaron Garba",
      role: "COO & Excellence",
      image: "https://readdy.ai/api/search-image?query=Professional%20memoji%20avatar%20of%20young%20African%20male%20operations%20executive%2C%20determined%20expression%2C%20modern%20business%20attire%2C%20tech-forward%20lighting%2C%203D%20cartoon%20style%20memoji%20character%2C%20operational%20excellence%20focus%2C%20clean%20background&width=400&height=500&seq=27&orientation=portrait",
      quote: "Excellence lives in the details of execution.",
      bio: "Operations virtuoso ensuring flawless delivery of transformative programs. Obsessed with turning vision into reality.",
      philosophy: "Perfection isn't a destination — it's a daily practice."
    }
  ];

  const coreValues = [
    {
      icon: "ri-lightbulb-flash-line",
      title: "Radical Curiosity",
      description: "We question everything, challenge assumptions, and explore the impossible.",
      color: "from-purple-500 to-pink-500"
    },
    {
      icon: "ri-hammer-line",
      title: "Obsessive Craftsmanship",
      description: "We don't just build — we architect experiences that transform lives.",
      color: "from-blue-500 to-cyan-500"
    },
    {
      icon: "ri-team-fill",
      title: "Collective Genius",
      description: "Individual brilliance multiplied by collaborative power creates magic.",
      color: "from-green-500 to-emerald-500"
    },
    {
      icon: "ri-rocket-2-line",
      title: "Exponential Impact",
      description: "We measure success by the ripple effects of transformation we create.",
      color: "from-orange-500 to-red-500"
    },
    {
      icon: "ri-plant-line",
      title: "Infinite Growth",
      description: "Learning never stops. Growing never ends. Potential has no ceiling.",
      color: "from-indigo-500 to-purple-500"
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentQuestion((prev) => (prev + 1) % philosophicalQuestions.length);
    }, 4_000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    "name": "About TheSpire - Revolutionary Tech & Creative Powerhouse",
    "description": "Founded by visionary leaders Abdullahi Saidu and Aysha Idris, TheSpire transforms lives through revolutionary technology education and creative innovation in Africa.",
    "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}/about`,
    "mainEntity": {
      "@type": "Organization",
      "name": "TheSpire",
      "url": `${import.meta.env.VITE_SITE_URL || "https://thespire.com.ng"}`,
      "foundingDate": "2020",
      "founder": [
        {
          "@type": "Person",
          "name": "Abdullahi Saidu",
          "jobTitle": "Co-Founder & CEO"
        },
        {
          "@type": "Person",
          "name": "Aysha Idris",
          "jobTitle": "Co-Founder & CTO"
        }
      ],
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Wuse 2",
        "addressLocality": "Abuja",
        "addressRegion": "Federal Capital Territory",
        "addressCountry": "Nigeria"
      },
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+234-916-771-0173",
        "contactType": "customer service",
        "email": "info@thespire.com.ng"
      },
      "sameAs": [
        "https://wa.me/2349167710173"
      ],
      "description": "TheSpire is a revolutionary tech & creative powerhouse offering training, software development, and education technology solutions across Africa."
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white overflow-x-hidden">
      <SEO
        title="About TheSpire - Revolutionary Tech & Creative Powerhouse"
        description="Founded by visionary leaders Abdullahi Saidu and Aysha Idris, TheSpire transforms lives through revolutionary technology education and creative innovation in Africa."
        keywords="about thespire, founders, abdullahi saidu, aysha idris, tech education africa, revolutionary training, nigeria technology"
        structuredData={structuredData}
      />
      
      {/* Dynamic Background */}
      <div 
        className="fixed inset-0 opacity-30 pointer-events-none"
        style={{
          background: `radial-gradient(circle at ${mousePosition.x}px ${mousePosition.y}px, rgba(109, 3, 244, 0.3) 0%, rgba(0, 127, 255, 0.2) 50%, transparent 70%)`
        }}
      />

      {/* Floating Particles */}
      <div className="fixed inset-0 pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-purple-400 rounded-full animate-pulse"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 3}s`,
              animationDuration: `${2 + Math.random() * 3}s`
            }}
          />
        ))}
      </div>

      {/* Revolutionary Hero */}
      <section className="relative min-h-screen flex items-center justify-center pt-16 sm:pt-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center relative z-10 w-full">
          {/* Philosophical Question */}
          <div className="mb-6 sm:mb-8 h-12 sm:h-16 flex items-center justify-center">
            <p className="text-sm sm:text-base md:text-lg lg:text-xl text-purple-300 italic transition-all duration-1000 transform px-4 text-center">
              {philosophicalQuestions[currentQuestion]}
            </p>
          </div>

          {/* Main Title */}
          <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-8xl xl:text-9xl font-black mb-6 sm:mb-8 leading-none">
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 animate-pulse">
              REVOLUTIONARY
            </span>
            <span className="block text-white mt-2 sm:mt-4">
              MINDS
            </span>
          </h1>

          <div className="max-w-4xl mx-auto mb-8 sm:mb-12 px-4">
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed">
              We don't just teach technology — we architect the future. 
              <span className="text-purple-400 font-semibold"> Every mind we touch becomes a catalyst for transformation.</span>
            </p>
          </div>

          {/* Animated CTA */}
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center px-4">
            <button className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-sm sm:text-base lg:text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto">
              <span className="relative z-10">Discover Our Story</span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </button>
            <button className="group px-6 sm:px-8 py-3 sm:py-4 border-2 border-purple-400 rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-purple-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto">
              Meet the Visionaries
            </button>
          </div>
        </div>

        {/* Geometric Shapes */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-16 h-16 sm:w-32 sm:h-32 border border-purple-500/30 rotate-45 animate-spin-slow"></div>
          <div className="absolute bottom-1/4 right-1/4 w-12 h-12 sm:w-24 sm:h-24 border border-blue-500/30 rotate-12 animate-bounce"></div>
          <div className="absolute top-1/2 right-1/3 w-8 h-8 sm:w-16 sm:h-16 bg-gradient-to-r from-purple-500/20 to-blue-500/20 rounded-full animate-pulse"></div>
        </div>
      </section>

      {/* Mission Manifesto */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-16 items-center">
            <div className="relative order-2 lg:order-1">
              <div className="absolute -inset-4 bg-gradient-to-r from-purple-600/20 to-blue-600/20 rounded-3xl blur-xl"></div>
              <img 
                src="https://readdy.ai/api/search-image?query=African%20tech%20revolutionaries%20collaborating%20in%20futuristic%20workspace%2C%20holographic%20displays%20and%20advanced%20technology%2C%20purple%20and%20blue%20neon%20lighting%2C%20cinematic%20photography%2C%20innovation%20and%20transformation%20atmosphere&width=600&height=400&seq=8&orientation=landscape"
                alt="Revolutionary Mission"
                className="relative rounded-2xl shadow-2xl object-cover w-full h-48 sm:h-60 lg:h-80"
              />
            </div>
            
            <div className="space-y-6 sm:space-y-8 order-1 lg:order-2">
              <div>
                <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 sm:mb-6">
                  THE MISSION
                </h2>
                <div className="space-y-4 sm:space-y-6 text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 leading-relaxed">
                  <p>
                    <span className="text-purple-400 font-bold">We are not educators.</span> We are revolution architects.
                  </p>
                  <p>
                    <span className="text-blue-400 font-bold">We don't create students.</span> We forge digital warriors.
                  </p>
                  <p>
                    <span className="text-pink-400 font-bold">We don't teach courses.</span> We ignite transformations.
                  </p>
                  <p className="text-white font-semibold">
                    Every line of code written, every design crafted, every solution built — 
                    it's all part of a larger revolution. Africa's technological renaissance starts here.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values - Unconventional Grid */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 sm:mb-6">
              OUR DNA
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300">The principles that fuel our revolution</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {coreValues.map((value, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900/50 to-gray-800/50 backdrop-blur-sm border border-gray-700/50 p-6 sm:p-8 hover:scale-105 transition-all duration-500"
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <div className={`absolute inset-0 bg-gradient-to-br ${value.color} opacity-10`}></div>
                </div>
                
                <div className="relative z-10">
                  <div className={`w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r ${value.color} rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${value.icon} text-lg sm:text-xl lg:text-2xl text-white`}></i>
                  </div>
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-3 sm:mb-4">{value.title}</h3>
                  <p className="text-gray-300 leading-relaxed text-sm sm:text-base">{value.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leadership - Revolutionary Profiles */}
      <section className="py-12 sm:py-20 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400 mb-4 sm:mb-6">
              THE ARCHITECTS
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300">Visionaries reshaping Africa's technological destiny</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {leaders.map((leader, index) => (
              <div 
                key={index}
                className="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-800/80 backdrop-blur-sm border border-gray-700/50 hover:scale-105 transition-all duration-500 cursor-pointer"
                onMouseEnter={() => setHoveredLeader(index)}
                onMouseLeave={() => setHoveredLeader(null)}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img 
                    src={leader.image}
                    alt={leader.name}
                    className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
                </div>
                
                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6">
                  <h3 className="text-lg sm:text-xl lg:text-2xl font-bold text-white mb-1">{leader.name}</h3>
                  <p className="text-purple-400 font-semibold mb-3 text-sm sm:text-base">{leader.role}</p>
                  
                  <div className={`transition-all duration-500 ${hoveredLeader === index ? 'opacity-100 max-h-96' : 'opacity-0 max-h-0'} overflow-hidden`}>
                    <blockquote className="text-gray-300 italic mb-3 text-xs sm:text-sm">"{leader.quote}"</blockquote>
                    <p className="text-gray-400 text-xs sm:text-sm mb-3">{leader.bio}</p>
                    <p className="text-purple-300 text-xs sm:text-sm font-medium">{leader.philosophy}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Revolutionary CTA */}
      <section className="py-12 sm:py-20 relative">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/50 to-blue-900/50"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 sm:mb-8 leading-tight">
            READY TO JOIN THE
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
              REVOLUTION?
            </span>
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-gray-300 mb-8 sm:mb-12">
            The future doesn't wait. Neither should you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center">
            <a 
              href="https://wa.me/2349167710173?text=I'm%20ready%20to%20start%20my%20transformation%20with%20TheSpire!"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full font-bold text-sm sm:text-base lg:text-lg overflow-hidden transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
            >
              <span className="relative z-10">Start Your Transformation</span>
              <div className="absolute inset-0 bg-gradient-to-r from-pink-600 to-purple-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            </a>
            <a href="/contact" className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-purple-400 rounded-full font-bold text-sm sm:text-base lg:text-lg hover:bg-purple-400 hover:text-black transition-all duration-300 whitespace-nowrap cursor-pointer w-full sm:w-auto text-center">
              Connect With Us
            </a>
          </div>
        </div>
      </section>

      {/* WhatsApp Floating Button */}
      <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50">
        <a
          href="https://wa.me/2349167710173"
          target="_blank"
          rel="noopener noreferrer"
          className="w-12 h-12 sm:w-14 sm:h-14 bg-green-500 rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 transition-colors group"
        >
          <i className="ri-whatsapp-line text-white text-lg sm:text-xl lg:text-2xl group-hover:scale-110 transition-transform"></i>
        </a>
      </div>
    </div>
  );
}
